import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react'

import type { PermissionId } from '@/shared/access'

/** Who made a change, as the API's `createdByType` and `modifiedByType` say. */
export type ActorType = 'user' | 'apiClient' | 'system'

/** One actor: its type and id. */
export interface Actor {
  readonly type: ActorType
  readonly id: string
}

/** An actor's name, and its record page when it has one, linked only for a caller holding `permission` when given. */
export interface ActorName {
  readonly name: string
  readonly path?: string
  readonly permission?: PermissionId
}

/** Names many actors of one type in one call, keyed by id; an id left out has no name. */
export type ActorNameLookup = (ids: readonly string[]) => Promise<ReadonlyMap<string, ActorName>>

/** The types a feature can name: a system actor is always "System". */
export type NamedActorType = Exclude<ActorType, 'system'>

const LookupsContext = createContext<ReadonlyMap<NamedActorType, ActorNameLookup>>(new Map())

/**
 * Registers the name lookup for one actor type, from a feature's app-wide provider: Site
 * Management's for `user` (SM-12), Channels' for `apiClient` (CHN-04). Until one is registered,
 * that type shows as the type alone. Keep `lookup` the same function, as a new one looks up again.
 */
export function ActorNamesProvider(props: {
  readonly type: NamedActorType
  readonly lookup: ActorNameLookup
  readonly children: ReactNode
}) {
  const outer = use(LookupsContext)
  const { type, lookup } = props
  const lookups = useMemo(() => new Map(outer).set(type, lookup), [outer, type, lookup])
  return <LookupsContext value={lookups}>{props.children}</LookupsContext>
}

/** An actor's name as it loads: `undefined` while loading, `null` when it has none to show. */
export type ActorNameState = ActorName | null | undefined

interface Loaded {
  readonly key: string
  readonly names: ReadonlyMap<string, ActorName | null>
}

/**
 * Names `actors`, with one lookup call per actor type, however many actors share it. A system
 * actor, a type with no lookup, or one whose lookup fails gets `null`, and shows as its type.
 */
export function useActorNames(actors: readonly Actor[]): (actor: Actor) => ActorNameState {
  const lookups = use(LookupsContext)
  // A stable key, so a new array of the same actors is not looked up again.
  const key = [...new Set(actors.map(actorKey))].toSorted().join('|')
  const [loaded, setLoaded] = useState<Loaded>({ key: '', names: new Map() })

  useEffect(() => {
    let current = true
    const byType = new Map<NamedActorType, string[]>()
    for (const entry of key === '' ? [] : key.split('|')) {
      const actor = parseKey(entry)
      if (actor.type !== 'system' && lookups.has(actor.type)) {
        byType.set(actor.type, [...(byType.get(actor.type) ?? []), actor.id])
      }
    }

    const names = new Map<string, ActorName | null>()
    const pending = [...byType].map(async ([type, ids]) => {
      try {
        const result = await lookups.get(type)?.(ids)
        for (const id of ids) {
          names.set(`${type}:${id}`, result?.get(id) ?? null)
        }
      } catch {
        for (const id of ids) {
          names.set(`${type}:${id}`, null)
        }
      }
    })
    const publish = async () => {
      await Promise.all(pending)
      if (current) {
        setLoaded({ key, names })
      }
    }
    void publish()
    return () => {
      current = false
    }
  }, [key, lookups])

  return (actor: Actor) => {
    if (actor.type === 'system' || !lookups.has(actor.type)) {
      return null
    }
    return loaded.key === key ? (loaded.names.get(actorKey(actor)) ?? null) : undefined
  }
}

function actorKey(actor: Actor): string {
  return `${actor.type}:${actor.id}`
}

function parseKey(entry: string): Actor {
  const separator = entry.indexOf(':')
  return { type: entry.slice(0, separator) as ActorType, id: entry.slice(separator + 1) }
}
